/**
 * Capgo Bundle Cleanup Composable
 *
 * Handles cleanup of old Capgo bundles to prevent storage bloat.
 * Each Capgo release creates ~12-13MB bundle files that accumulate
 * if autoDeletePrevious doesn't work correctly.
 *
 * Usage:
 * - Call cleanupOldBundles() on app startup after notifyAppReady()
 * - Or call manually from settings/debug UI
 */

import { ref, computed } from 'vue'
import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { Filesystem, Directory } from '@capacitor/filesystem'

// State
const isCleaningUp = ref(false)
const lastCleanupResult = ref(null)
const cleanupError = ref(null)

export function useCapgoCleanup() {
  const isNativeApp = computed(() => Capacitor.isNativePlatform())

  /**
   * Get list of all Capgo bundles
   */
  const listBundles = async () => {
    if (!isNativeApp.value) return []

    try {
      const result = await CapacitorUpdater.list()
      const bundles = Array.isArray(result) ? result : result?.bundles || []
      console.log('[CapgoCleanup] Found bundles:', bundles.length)
      return bundles
    } catch (error) {
      console.error('[CapgoCleanup] Error listing bundles:', error)
      return []
    }
  }

  /**
   * Get the currently active bundle
   */
  const getCurrentBundle = async () => {
    if (!isNativeApp.value) return null

    try {
      const current = await CapacitorUpdater.getCurrentBundle()
      console.log('[CapgoCleanup] Current bundle:', current)
      return current
    } catch (error) {
      console.error('[CapgoCleanup] Error getting current bundle:', error)
      return null
    }
  }

  /**
   * Delete a specific bundle by ID
   */
  const deleteBundle = async (bundleId) => {
    if (!isNativeApp.value) return false

    try {
      await CapacitorUpdater.delete({ id: bundleId })
      console.log('[CapgoCleanup] Deleted bundle:', bundleId)
      return true
    } catch (error) {
      console.error('[CapgoCleanup] Error deleting bundle:', bundleId, error)
      return false
    }
  }

  /**
   * Clean up old bundles, keeping only the current active one
   * Returns cleanup result with stats
   */
  const cleanupOldBundles = async () => {
    if (!isNativeApp.value) {
      return { success: false, reason: 'Not a native app' }
    }

    if (isCleaningUp.value) {
      return { success: false, reason: 'Cleanup already in progress' }
    }

    isCleaningUp.value = true
    cleanupError.value = null

    const result = {
      success: true,
      bundlesDeleted: 0,
      bundlesKept: 0,
      bundlesFailed: 0,
      deletedIds: [],
      keptIds: [],
      failedIds: [],
      bytesFreed: 0
    }

    try {
      // Get current bundle first
      const currentBundle = await getCurrentBundle()
      const currentId = currentBundle?.id || currentBundle?.version

      if (!currentId) {
        // If no current bundle, might be running from built-in bundle
        console.log(
          '[CapgoCleanup] No current bundle ID - running from built-in bundle'
        )
      }

      // List all bundles
      const bundles = await listBundles()

      if (bundles.length === 0) {
        result.reason = 'No bundles found'
        lastCleanupResult.value = result
        isCleaningUp.value = false
        return result
      }

      console.log(
        `[CapgoCleanup] Processing ${bundles.length} bundles, current: ${currentId}`
      )

      // Process each bundle
      for (const bundle of bundles) {
        const bundleId = bundle.id || bundle.version
        const isActive = bundleId === currentId

        // Keep the current active bundle and any that are being downloaded/pending
        const shouldKeep =
          isActive ||
          bundle.status === 'downloading' ||
          bundle.status === 'pending' ||
          bundle.status === 'downloaded'

        if (shouldKeep) {
          result.bundlesKept++
          result.keptIds.push(bundleId)
          console.log(
            `[CapgoCleanup] Keeping bundle: ${bundleId} (status: ${bundle.status})`
          )
          continue
        }

        // Try to delete old bundle
        const deleted = await deleteBundle(bundleId)
        if (deleted) {
          result.bundlesDeleted++
          result.deletedIds.push(bundleId)
          // Estimate ~12-13MB per bundle based on user's observed file sizes
          result.bytesFreed += 12.5 * 1024 * 1024
        } else {
          result.bundlesFailed++
          result.failedIds.push(bundleId)
        }
      }

      console.log('[CapgoCleanup] Bundle cleanup complete:', result)
    } catch (error) {
      console.error('[CapgoCleanup] Error during cleanup:', error)
      result.success = false
      result.error = error.message
      cleanupError.value = error.message
    }

    lastCleanupResult.value = result
    isCleaningUp.value = false
    return result
  }

  /**
   * Scan for orphaned Capgo files in Documents/Data directories
   * These are files that match Capgo's naming pattern but aren't in the bundle list
   */
  const findOrphanedFiles = async () => {
    if (!isNativeApp.value) return { documents: [], data: [] }

    const orphaned = {
      documents: [],
      data: []
    }

    try {
      // Get list of known bundle IDs
      const bundles = await listBundles()
      const knownIds = new Set(bundles.map((b) => b.id || b.version))
      const currentBundle = await getCurrentBundle()
      if (currentBundle?.id) knownIds.add(currentBundle.id)

      // Pattern for Capgo bundle directories (10 character alphanumeric names)
      const isCapgoPattern = (name) => /^[a-zA-Z0-9]{10}$/.test(name)

      // Check Documents directory
      try {
        const docFiles = await Filesystem.readdir({
          path: '',
          directory: Directory.Documents
        })

        for (const file of docFiles.files) {
          if (isCapgoPattern(file.name) && !knownIds.has(file.name)) {
            orphaned.documents.push({
              name: file.name,
              size: file.size || 0,
              mtime: file.mtime,
              path: file.name
            })
          }
        }
      } catch (err) {
        console.log('[CapgoCleanup] Could not read Documents:', err.message)
      }

      // Check Data directory
      try {
        const dataFiles = await Filesystem.readdir({
          path: '',
          directory: Directory.Data
        })

        for (const file of dataFiles.files) {
          if (isCapgoPattern(file.name) && !knownIds.has(file.name)) {
            orphaned.data.push({
              name: file.name,
              size: file.size || 0,
              mtime: file.mtime,
              path: file.name
            })
          }
        }
      } catch (err) {
        console.log('[CapgoCleanup] Could not read Data:', err.message)
      }

      console.log('[CapgoCleanup] Found orphaned files:', {
        documents: orphaned.documents.length,
        data: orphaned.data.length
      })
    } catch (error) {
      console.error('[CapgoCleanup] Error finding orphaned files:', error)
    }

    return orphaned
  }

  /**
   * Delete orphaned Capgo files from Documents/Data directories
   * WARNING: Use with caution - only deletes files matching Capgo pattern
   */
  const cleanupOrphanedFiles = async () => {
    if (!isNativeApp.value) {
      return { success: false, reason: 'Not a native app' }
    }

    const result = {
      success: true,
      documentsDeleted: 0,
      dataDeleted: 0,
      bytesFreed: 0,
      errors: []
    }

    try {
      const orphaned = await findOrphanedFiles()

      // Delete orphaned files in Documents
      for (const file of orphaned.documents) {
        try {
          await Filesystem.rmdir({
            path: file.path,
            directory: Directory.Documents,
            recursive: true
          })
          result.documentsDeleted++
          result.bytesFreed += file.size || 12.5 * 1024 * 1024
          console.log('[CapgoCleanup] Deleted orphaned Documents:', file.name)
        } catch (err) {
          // Try deleteFile if rmdir fails (might be a file not directory)
          try {
            await Filesystem.deleteFile({
              path: file.path,
              directory: Directory.Documents
            })
            result.documentsDeleted++
            result.bytesFreed += file.size || 12.5 * 1024 * 1024
            console.log(
              '[CapgoCleanup] Deleted orphaned Documents file:',
              file.name
            )
          } catch (err2) {
            result.errors.push(`Documents/${file.name}: ${err2.message}`)
          }
        }
      }

      // Delete orphaned files in Data
      for (const file of orphaned.data) {
        try {
          await Filesystem.rmdir({
            path: file.path,
            directory: Directory.Data,
            recursive: true
          })
          result.dataDeleted++
          result.bytesFreed += file.size || 12.5 * 1024 * 1024
          console.log('[CapgoCleanup] Deleted orphaned Data:', file.name)
        } catch (err) {
          // Try deleteFile if rmdir fails
          try {
            await Filesystem.deleteFile({
              path: file.path,
              directory: Directory.Data
            })
            result.dataDeleted++
            result.bytesFreed += file.size || 12.5 * 1024 * 1024
            console.log('[CapgoCleanup] Deleted orphaned Data file:', file.name)
          } catch (err2) {
            result.errors.push(`Data/${file.name}: ${err2.message}`)
          }
        }
      }

      console.log('[CapgoCleanup] Orphan cleanup complete:', result)
    } catch (error) {
      console.error('[CapgoCleanup] Error cleaning orphaned files:', error)
      result.success = false
      result.error = error.message
    }

    return result
  }

  /**
   * Full cleanup - cleans both Capgo bundles and orphaned files
   */
  const fullCleanup = async () => {
    const bundleResult = await cleanupOldBundles()
    const orphanResult = await cleanupOrphanedFiles()

    return {
      bundles: bundleResult,
      orphans: orphanResult,
      totalBytesFreed:
        (bundleResult.bytesFreed || 0) + (orphanResult.bytesFreed || 0)
    }
  }

  /**
   * Get cleanup status info
   */
  const getCleanupInfo = async () => {
    const bundles = await listBundles()
    const currentBundle = await getCurrentBundle()
    const orphaned = await findOrphanedFiles()

    const currentId = currentBundle?.id || currentBundle?.version

    // Count bundles that would be deleted
    const deletableBundles = bundles.filter((b) => {
      const bundleId = b.id || b.version
      return (
        bundleId !== currentId &&
        b.status !== 'downloading' &&
        b.status !== 'pending' &&
        b.status !== 'downloaded'
      )
    })

    return {
      totalBundles: bundles.length,
      currentBundle: currentId,
      deletableBundles: deletableBundles.length,
      orphanedDocuments: orphaned.documents.length,
      orphanedData: orphaned.data.length,
      estimatedSavings:
        (deletableBundles.length +
          orphaned.documents.length +
          orphaned.data.length) *
        12.5 *
        1024 *
        1024
    }
  }

  return {
    // State
    isCleaningUp: computed(() => isCleaningUp.value),
    lastCleanupResult: computed(() => lastCleanupResult.value),
    cleanupError: computed(() => cleanupError.value),

    // Methods
    listBundles,
    getCurrentBundle,
    deleteBundle,
    cleanupOldBundles,
    findOrphanedFiles,
    cleanupOrphanedFiles,
    fullCleanup,
    getCleanupInfo
  }
}
